/**
 * Provee la sesion del SSO al arbol de componentes.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AuthContext, type AuthContextType } from './AuthContext';
import type { AuthSession } from '@domain/services';
import { getAuthService, getGetMyRolesUseCase } from '@di';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const authService = useMemo(() => getAuthService(), []);

  /**
   * Los roles del claim son una foto del momento en que se emitio el token.
   * Para autorizar la consola se pregunta a la API, que es la fuente de verdad:
   * un rol revocado deja de verse sin esperar al refresh.
   */
  const withFreshRoles = useCallback(async (current: AuthSession): Promise<AuthSession> => {
    try {
      const roles = await getGetMyRolesUseCase().execute();
      return { ...current, roles: roles.filter(r => !r.applicationId).map(r => r.code) };
    } catch {
      return current;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Al montar se intenta recuperar la sesion en silencio: si el usuario ya
    // entro en otra aplicacion, la cookie del SSO alcanza y no hay login.
    const restore = async () => {
      try {
        const current = await authService.getSession();
        if (cancelled) return;

        setSession(current ? await withFreshRoles(current) : null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
  }, [authService, withFreshRoles]);

  const login = useCallback(async () => {
    await authService.login();
  }, [authService]);

  const completeLogin = useCallback(async () => {
    setIsLoading(true);
    try {
      setSession(await withFreshRoles(await authService.completeLogin()));
    } finally {
      setIsLoading(false);
    }
  }, [authService, withFreshRoles]);

  const logout = useCallback(async () => {
    // No se limpia el estado antes de tiempo: al quedar "sin sesion" la ruta
    // protegida redirige a /login, que arranca un login nuevo y le gana la
    // carrera al redirect de cierre de sesion. El estado se va con la pagina.
    await authService.logout();
  }, [authService]);

  const hasRole = useCallback(
    (...roles: string[]) => roles.some(role => session?.roles.includes(role) ?? false),
    [session]
  );

  const value: AuthContextType = {
    session,
    isAuthenticated: !!session,
    isLoading,
    login,
    completeLogin,
    logout,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
